import React, { useEffect, useRef } from "react";

/**
 * Premium animated 3D wireframe wave terrain.
 * Renders a perspective-projected grid whose surface undulates over time,
 * creating a sophisticated, business-grade depth effect (no flat looping lines).
 */
export const WaveMesh = ({ className = "" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let raf;
    let t = 0;

    const COLS = 48;
    const ROWS = 28;

    const resize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    // Project a grid coordinate (gx: -1..1, gz: 0 far .. 1 near) into screen space.
    const project = (gx, gz, time) => {
      const depth = gz;
      const scale = 0.3 + depth * 1.05;
      const horizon = h * 0.42;
      const bottom = h * 0.99;
      const baseY = horizon + Math.pow(depth, 1.3) * (bottom - horizon);
      const wave =
        Math.sin(gx * 2.6 + time) * 0.6 +
        Math.sin(gx * 1.3 - time * 0.65) * 0.24 +
        Math.sin(gz * 5.5 + time * 1.05) * 0.55 +
        Math.cos(gx * 4.2 + gz * 3.0 + time * 0.8) * 0.2;
      const amp = 36;
      const px = w * 0.5 + gx * (w * 0.66) * scale;
      const py = baseY - wave * amp * scale;
      return { px, py, depth };
    };

    const loop = () => {
      ctx.clearRect(0, 0, w, h);
      t += 0.006;

      // Vertical contour lines (give the 3D ribs / depth convergence)
      for (let i = 0; i < COLS; i += 1) {
        const gx = (i / (COLS - 1)) * 2 - 1;
        ctx.beginPath();
        for (let j = 0; j < ROWS; j++) {
          const gz = j / (ROWS - 1);
          const { px, py } = project(gx, gz, t);
          if (j === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.strokeStyle = "hsla(26, 100%, 58%, 0.07)";
        ctx.lineWidth = 0.6;
        ctx.stroke();
      }

      // Horizontal rows (far -> near so nearer lines paint on top, brighter)
      for (let j = 0; j < ROWS; j++) {
        const gz = j / (ROWS - 1);
        ctx.beginPath();
        for (let i = 0; i < COLS; i++) {
          const gx = (i / (COLS - 1)) * 2 - 1;
          const { px, py } = project(gx, gz, t);
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        const a = 0.07 + Math.pow(gz, 1.3) * 0.42;
        const hue = 25 + gz * 6;
        ctx.strokeStyle = `hsla(${hue}, 100%, 56%, ${a})`;
        ctx.lineWidth = 0.6 + gz * 1.8;
        if (gz > 0.6) {
          ctx.shadowColor = "hsla(28, 100%, 55%, 0.8)";
          ctx.shadowBlur = 10;
        } else {
          ctx.shadowBlur = 0;
        }
        ctx.stroke();
      }
      ctx.shadowBlur = 0;

      raf = requestAnimationFrame(loop);
    };
    loop();

    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
    />
  );
};
